import type { Participant, CallLayout } from './callRoom';
import { ParticipantTile, TileSize } from './Tile';
import { Mosaic, useAspects } from './Mosaic';
import type { MosaicConfig } from './mosaicMath';

/*
 * Сцена звонка без показа экрана: либо плитки мозаикой, либо один человек
 * крупно.
 *
 * Мозаика одна на все устройства (решение Ильи 16.09.2026): плитка получает
 * форму своего кадра, строка — общую высоту, и раскладка складывается в
 * прямоугольник по центру. Прежде на телефоне были страницы по четыре плитки
 * с листанием — они отменены вместе с равными клетками.
 *
 * Порядок плиток — порядок участников с сервера (по приглашению и входу), он
 * не зависит от того, кто говорит.
 */

/** На телефоне плиток в строке меньше: иначе лица мельчают до нечитаемых. */
const MOBILE_MOSAIC: Partial<MosaicConfig> = { maxPerRow: 3, minTileW: 84, minTileH: 64 };

/** Чем больше плиток, тем мельче фотография: в сетке 4×3 крупная не влезает. */
function faceSize(n: number, mobile: boolean): TileSize {
  const big = mobile ? 1 : 2;
  return n <= big ? 'lg' : n <= 6 ? 'md' : 'sm';
}

export function Grid({
  people,
  layout,
  mainId,
  mobile,
  pinned,
  onPick,
}: {
  /** Кого показывать: активные и те, кому ещё звонят. */
  people: Participant[];
  layout: CallLayout;
  /** Кто крупно в раскладке «докладчик». */
  mainId: string | null;
  mobile: boolean;
  /** Крупный закреплён смотрящим, а не выбран по голосу. */
  pinned?: boolean;
  /** Нажатие по плитке: на телефоне открывает крупный вид. */
  onPick?: (p: Participant) => void;
}) {
  if (layout === 'speaker') {
    const main = people.find((p) => p.id === mainId) ?? people[0];
    return (
      <div className="call-main">
        {main && <ParticipantTile p={main} size="lg" backdrop pinned={pinned} />}
        {/* На телефоне под сценой сказано, кто говорит: имя на самой плитке
            мелкое, а полоса миниатюр внизу забирает внимание (макет Ильи). */}
        {mobile && main && (
          <div className="call-speaking">{main.speaking ? `${main.name} говорит` : main.name}</div>
        )}
      </div>
    );
  }

  return <CallMosaic people={people} mobile={mobile} onPick={onPick} />;
}

/**
 * Мозаика плиток. Одна и та же на компьютере и на телефоне (решение Ильи
 * 16.09.2026): раскладка считается по форме кадров, а не по числу клеток, и
 * телефону это нужно не меньше. Прежние страницы по четыре плитки на телефоне
 * отменены — мозаика умещает всех и без листания.
 *
 * Пропорции кадров собираются с самих видеоэлементов (`useAspects`), поэтому
 * плитка принимает форму того, что реально приходит с камеры. Выключенная
 * камера пропорцию не сбрасывает: плитка не меняет размер под аватаром.
 */
function CallMosaic({
  people,
  mobile,
  onPick,
}: {
  people: Participant[];
  mobile: boolean;
  onPick?: (p: Participant) => void;
}) {
  const { aspects, report } = useAspects();
  const size = faceSize(people.length, mobile);
  const items = people.map((p) => ({ id: p.id, aspect: aspects[p.id] }));
  return (
    <Mosaic
      className="call-mosaic"
      items={items}
      config={mobile ? MOBILE_MOSAIC : undefined}
      render={(id, tile) => {
        const p = people.find((x) => x.id === id);
        if (!p) return null;
        return (
          <ParticipantTile
            p={p}
            size={size}
            fit={tile.fit}
            onAspect={(a) => report(p.id, a)}
            onPick={onPick ? () => onPick(p) : undefined}
          />
        );
      }}
    />
  );
}
